/**
 * Service for managing Order API operations
 * Implements all endpoints from the Orders API Frontend Integration Guide
 * 
 * Orders represent laboratory work requests linked to clients.
 * Each order has a unique ID in format YYMM#### (e.g., 25120001)
 */

import { makeAuthenticatedRequest } from './backendApiService';
import { convertUTCDatesToLocal } from '../utils/dateUtils';

const ORDERS_BASE_URL = '/api/orders';

/**
 * Helper to convert dates in order objects from UTC to local
 * @param {Object|Array} data - Order or array of orders
 * @returns {Object|Array} Data with converted dates
 */
const convertOrderDates = (data) => {
    return convertUTCDatesToLocal(data, ['createdAt', 'orderDate', 'receiveDate', 'reportDate', 'lastModifiedAt']);
};

/**
 * Helper to convert dates in paginated response
 * @param {Object} pageResponse - Paginated response with content array
 * @returns {Object} Page response with converted dates in content
 */
const convertPagedOrderDates = (pageResponse) => {
    if (pageResponse && pageResponse.content && Array.isArray(pageResponse.content)) {
        return {
            ...pageResponse,
            content: convertOrderDates(pageResponse.content)
        };
    }
    return pageResponse;
};

/**
 * Order Service Class
 */
class OrderService {
    /**
     * Create a new order with auto-generated ID
     * CRITICAL: This endpoint is optimized for concurrent use in multi-user environments
     * @param {string} accessToken - JWT access token
     * @param {import('../types/orders.types').CreateOrderRequest} orderData - Order creation data
     * @returns {Promise<import('../types/orders.types').Order>} Created order object
     */
    async createOrder(accessToken, orderData) {
        try {
            const response = await makeAuthenticatedRequest(accessToken, ORDERS_BASE_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(orderData)
            });
            return convertOrderDates(response);
        } catch (error) {
            console.error('Failed to create order:', error);
            throw error;
        }
    }

    /**
     * Get all orders with client information
     * @param {string} accessToken - JWT access token
     * @returns {Promise<import('../types/orders.types').Order[]>} Array of orders
     */
    async getAllOrders(accessToken) {
        try {
            const response = await makeAuthenticatedRequest(accessToken, ORDERS_BASE_URL);
            return convertOrderDates(response);
        } catch (error) {
            console.error('Failed to fetch all orders:', error);
            throw error;
        }
    }

    /**
     * Get a specific order by ID
     * @param {string} accessToken - JWT access token
     * @param {string} orderId - Order ID (e.g., "25120001")
     * @returns {Promise<import('../types/orders.types').Order>} Order object
     */
    async getOrderById(accessToken, orderId) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${ORDERS_BASE_URL}/${orderId}`
            );
            return convertOrderDates(response);
        } catch (error) {
            console.error(`Failed to fetch order ${orderId}:`, error);
            throw error;
        }
    }

    /**
     * Get all orders for a specific client
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @returns {Promise<import('../types/orders.types').Order[]>} Array of orders (sorted newest first)
     */
    async getOrdersByClient(accessToken, clientId) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${ORDERS_BASE_URL}/client/${clientId}`
            );
            return convertOrderDates(response);
        } catch (error) {
            console.error(`Failed to fetch orders for client ${clientId}:`, error);
            throw error;
        }
    }



    /**
     * Get orders within a date range
     * @param {string} accessToken - JWT access token
     * @param {string} startDate - Start date (ISO format YYYY-MM-DD)
     * @param {string} endDate - End date (ISO format YYYY-MM-DD)
     * @returns {Promise<import('../types/orders.types').Order[]>} Array of orders
     */
    async getOrdersByDateRange(accessToken, startDate, endDate) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${ORDERS_BASE_URL}/date-range?startDate=${startDate}&endDate=${endDate}`
            );
            return convertOrderDates(response);
        } catch (error) {
            console.error(`Failed to fetch orders for date range ${startDate} to ${endDate}:`, error);
            throw error;
        }
    }

    /**
     * Get orders for a specific month
     * @param {string} accessToken - JWT access token
     * @param {string} yearMonth - Year and month in YYMM format (e.g., "2512")
     * @returns {Promise<import('../types/orders.types').Order[]>} Array of orders
     */
    async getOrdersByMonth(accessToken, yearMonth) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${ORDERS_BASE_URL}/month/${yearMonth}`
            );
            return convertOrderDates(response);
        } catch (error) {
            console.error(`Failed to fetch orders for month ${yearMonth}:`, error);
            throw error;
        }
    }

    /**
     * Get recent orders from the last N days
     * @param {string} accessToken - JWT access token
     * @param {number} [days=30] - Number of days (default: 30)
     * @returns {Promise<import('../types/orders.types').Order[]>} Array of orders
     */
    async getRecentOrders(accessToken, days = 30) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${ORDERS_BASE_URL}/recent?days=${days}`
            );
            return convertOrderDates(response);
        } catch (error) {
            console.error(`Failed to fetch recent orders (${days} days):`, error);
            throw error;
        }
    }

    /**
     * Search orders by order ID, client name, or client code
     * @param {string} accessToken - JWT access token
     * @param {string} searchTerm - Search term
     * @returns {Promise<import('../types/orders.types').Order[]>} Array of matching orders
     */
    async searchOrders(accessToken, searchTerm) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${ORDERS_BASE_URL}/search?q=${encodeURIComponent(searchTerm)}`
            );
            return convertOrderDates(response);
        } catch (error) {
            console.error(`Failed to search orders with term "${searchTerm}":`, error);
            throw error;
        }
    }

    /**
     * Update an existing order
     * Can update dates, client, and boolean flags (processed, completed, reviewFlag)
     * @param {string} accessToken - JWT access token
     * @param {string} orderId - Order ID
     * @param {import('../types/orders.types').UpdateOrderRequest} updateData - Updated order data
     * @returns {Promise<import('../types/orders.types').Order>} Updated order object
     */
    async updateOrder(accessToken, orderId, updateData) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${ORDERS_BASE_URL}/${orderId}`, 
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(updateData)
                }
            );
            return convertOrderDates(response);
        } catch (error) {
            console.error(`Failed to update order ${orderId}:`, error);
            throw error;
        }
    }

    /**
     * Delete an order
     * @param {string} accessToken - JWT access token
     * @param {string} orderId - Order ID
     * @returns {Promise<void>}
     */
    async deleteOrder(accessToken, orderId) {
        try {
            await makeAuthenticatedRequest(
                accessToken, 
                `${ORDERS_BASE_URL}/${orderId}`, 
                {
                    method: 'DELETE'
                }
            );
        } catch (error) {
            console.error(`Failed to delete order ${orderId}:`, error);
            throw error;
        }
    }

    /**
     * Check if an order exists (lightweight HEAD request)
     * @param {string} accessToken - JWT access token
     * @param {string} orderId - Order ID
     * @returns {Promise<boolean>} True if exists, false otherwise
     */
    async orderExists(accessToken, orderId) {
        try {
            await makeAuthenticatedRequest(
                accessToken, 
                `${ORDERS_BASE_URL}/${orderId}`, 
                {
                    method: 'HEAD'
                }
            );
            return true;
        } catch (error) {
            if (error.message && error.message.includes('404')) {
                return false;
            }
            throw error;
        }
    }

    /**
     * Get order count for a specific client
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @returns {Promise<number>} Order count
     */
    async countByClient(accessToken, clientId) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${ORDERS_BASE_URL}/count/client/${clientId}`
            );
            return response.orderCount;
        } catch (error) {
            console.error(`Failed to count orders for client ${clientId}:`, error);
            throw error;
        }
    }



    // ============= PAGINATED ENDPOINTS =============

    /**
     * Get all orders (paginated)
     * @param {string} accessToken - JWT access token
     * @param {PageParams} params - Pagination parameters
     * @returns {Promise<Page>} Paginated orders
     */
    async getAllOrdersPaged(accessToken, params = {}) {
        try {
            const queryParams = new URLSearchParams({
                page: params.page?.toString() || '0',
                size: params.size?.toString() || '20',
                sort: params.sort || 'orderDate,desc'
            });
            
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${ORDERS_BASE_URL}/page?${queryParams.toString()}`
            );
            return convertPagedOrderDates(response);
        } catch (error) {
            console.error('Failed to fetch paginated orders:', error);
            throw error;
        }
    }

    /**
     * Get orders by client ID (paginated)
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {PageParams} params - Pagination parameters
     * @returns {Promise<Page>} Paginated orders
     */
    async getOrdersByClientPaged(accessToken, clientId, params = {}) {
        try {
            const queryParams = new URLSearchParams({
                page: params.page?.toString() || '0',
                size: params.size?.toString() || '20',
                sort: params.sort || 'orderDate,desc'
            });
            
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${ORDERS_BASE_URL}/client/${clientId}/page?${queryParams.toString()}`
            );
            return convertPagedOrderDates(response);
        } catch (error) {
            console.error(`Failed to fetch paginated orders for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Get orders by date range (paginated)
     * @param {string} accessToken - JWT access token
     * @param {string} startDate - Start date (YYYY-MM-DD)
     * @param {string} endDate - End date (YYYY-MM-DD)
     * @param {PageParams} params - Pagination parameters
     * @returns {Promise<Page>} Paginated orders
     */
    async getOrdersByDateRangePaged(accessToken, startDate, endDate, params = {}) {
        try {
            const queryParams = new URLSearchParams({
                startDate,
                endDate,
                page: params.page?.toString() || '0',
                size: params.size?.toString() || '20',
                sort: params.sort || 'orderDate,desc'
            });
            
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${ORDERS_BASE_URL}/date-range/page?${queryParams.toString()}`
            );
            return convertPagedOrderDates(response);
        } catch (error) {
            console.error(`Failed to fetch paginated orders for date range ${startDate} to ${endDate}:`, error);
            throw error;
        }
    }

    /**
     * Get orders by month (paginated)
     * @param {string} accessToken - JWT access token
     * @param {string} yearMonth - Year-month (YYYY-MM)
     * @param {PageParams} params - Pagination parameters
     * @returns {Promise<Page>} Paginated orders
     */
    async getOrdersByMonthPaged(accessToken, yearMonth, params = {}) {
        try {
            const queryParams = new URLSearchParams({
                page: params.page?.toString() || '0',
                size: params.size?.toString() || '20',
                sort: params.sort || 'orderDate,desc'
            });
            
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${ORDERS_BASE_URL}/month/${yearMonth}/page?${queryParams.toString()}`
            );
            return convertPagedOrderDates(response);
        } catch (error) {
            console.error(`Failed to fetch paginated orders for month ${yearMonth}:`, error);
            throw error;
        }
    }

    /**
     * Get recent orders (paginated)
     * @param {string} accessToken - JWT access token
     * @param {number} days - Number of days
     * @param {PageParams} params - Pagination parameters
     * @returns {Promise<Page>} Paginated orders
     */
    async getRecentOrdersPaged(accessToken, days = 30, params = {}) {
        try {
            const queryParams = new URLSearchParams({
                page: params.page?.toString() || '0',
                size: params.size?.toString() || '20',
                sort: params.sort || 'orderDate,desc'
            });
            
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${ORDERS_BASE_URL}/recent/${days}/page?${queryParams.toString()}`
            );
            return convertPagedOrderDates(response);
        } catch (error) {
            console.error(`Failed to fetch recent paginated orders (${days} days):`, error);
            throw error;
        }
    }

    /**
     * Search orders (paginated)
     * @param {string} accessToken - JWT access token
     * @param {string} query - Search query
     * @param {PageParams} params - Pagination parameters
     * @returns {Promise<Page>} Paginated orders
     */
    async searchOrdersPaged(accessToken, query, params = {}) {
        try {
            const queryParams = new URLSearchParams({
                query,
                page: params.page?.toString() || '0',
                size: params.size?.toString() || '20',
                sort: params.sort || 'orderDate,desc'
            });
            
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${ORDERS_BASE_URL}/search/page?${queryParams.toString()}`
            );
            return convertPagedOrderDates(response);
        } catch (error) {
            console.error(`Failed to search paginated orders with query "${query}":`, error);
            throw error;
        }
    }

    /**
     * Get orders by processed status (paginated)
     * @param {string} accessToken - JWT access token
     * @param {boolean} processed - Processed status
     * @param {PageParams} params - Pagination parameters
     * @returns {Promise<Page>} Paginated orders
     */
    async getOrdersByProcessed(accessToken, processed, params = {}) {
        try {
            const queryParams = new URLSearchParams({
                page: params.page?.toString() || '0',
                size: params.size?.toString() || '20',
                sort: params.sort || 'orderDate,desc'
            });
            
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${ORDERS_BASE_URL}/processed/${processed}/page?${queryParams.toString()}`
            );
            return convertPagedOrderDates(response);
        } catch (error) {
            console.error(`Failed to fetch paginated orders by processed=${processed}:`, error);
            throw error;
        }
    }

    /**
     * Get orders by completed status (paginated)
     * @param {string} accessToken - JWT access token
     * @param {boolean} completed - Completed status
     * @param {PageParams} params - Pagination parameters
     * @returns {Promise<Page>} Paginated orders
     */
    async getOrdersByCompleted(accessToken, completed, params = {}) {
        try {
            const queryParams = new URLSearchParams({
                page: params.page?.toString() || '0',
                size: params.size?.toString() || '20',
                sort: params.sort || 'orderDate,desc'
            });
            
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${ORDERS_BASE_URL}/completed/${completed}/page?${queryParams.toString()}`
            );
            return convertPagedOrderDates(response);
        } catch (error) {
            console.error(`Failed to fetch paginated orders by completed=${completed}:`, error);
            throw error;
        }
    }

    /**
     * Get orders that need review (paginated)
     * @param {string} accessToken - JWT access token
     * @param {PageParams} params - Pagination parameters
     * @returns {Promise<Page>} Paginated orders
     */
    async getOrdersNeedingReview(accessToken, params = {}) {
        try {
            const queryParams = new URLSearchParams({
                page: params.page?.toString() || '0',
                size: params.size?.toString() || '20',
                sort: params.sort || 'orderDate,desc'
            });
            
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${ORDERS_BASE_URL}/needs-review/page?${queryParams.toString()}`
            );
            return convertPagedOrderDates(response);
        } catch (error) {
            console.error('Failed to fetch paginated orders needing review:', error);
            throw error;
        }
    }

    /**
     * Get total order count
     * @param {string} accessToken - JWT access token
     * @returns {Promise<number>} Total order count
     */
    async getTotalCount(accessToken) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${ORDERS_BASE_URL}/count`
            );
            return response.totalCount;
        } catch (error) {
            console.error('Failed to fetch total order count:', error);
            throw error;
        }
    }

    // ============= HELPER METHODS =============

    /**
     * Helper: Get today's date in ISO format (YYYY-MM-DD)
     * @returns {string} Today's date
     */
    getTodayDate() {
        return new Date().toISOString().split('T')[0];
    }

    /**
     * Helper: Format order ID for display
     * @param {string} orderId - Order ID
     * @returns {string} Formatted order ID
     */
    formatOrderId(orderId) {
        return orderId; // Already in YYMM#### format
    }
}

// Export singleton instance
const orderService = new OrderService();
export default orderService;
